import { useCallback, useEffect, useRef, useState } from "react";
import { useWallet } from "@aptos-labs/wallet-adapter-react";
import { Hex, isUserTransactionResponse } from "@aptos-labs/ts-sdk";
import { getBkfcAptos } from "./aptos";
import { requireBkfcModuleAddress } from "./config";
import {
  confirmMint,
  fetchClaimSignature,
  BkfcConfirmMintResponse,
} from "./api";
import { getRecaptchaToken, warmRecaptchaToken } from "./recaptcha";
import {
  classifyGasStationError,
  classifySignerError,
  BkfcClassifiedError,
} from "./errors";

// Adapted from the Aptos reference app's useClaim (apps/web/src/hooks/useClaim.ts),
// with finer-grained statuses because the BKFC Figma flow shows dedicated
// screens for each stage:
//   idle
//   requesting-signature   "Approve in your wallet" (step 1 active)
//   awaiting-approval      "Approve in your wallet" (step 2 active — wallet is open)
//   waiting-confirmation   "Minting your certificate" (tx submitted, waiting for chain)
//   verifying              "Minting your certificate" (backend verifies on chain)
//   confirmed              "Certificate claimed"
//   error
export type BkfcClaimStatus =
  | "idle"
  | "requesting-signature"
  | "awaiting-approval"
  | "waiting-confirmation"
  | "verifying"
  | "confirmed"
  | "error";

export interface BkfcClaimResult {
  hash: string;
  tokenAddress: string | null;
  serial: number;
}

interface UseBkfcClaimResult {
  claim: (email: string) => Promise<void>;
  status: BkfcClaimStatus;
  result: BkfcClaimResult | null;
  error: BkfcClassifiedError | null;
  reset: () => void;
}

export function useBkfcClaim(uid: string | null): UseBkfcClaimResult {
  const { account, signAndSubmitTransaction } = useWallet();
  const [status, setStatus] = useState<BkfcClaimStatus>("idle");
  const [result, setResult] = useState<BkfcClaimResult | null>(null);
  const [error, setError] = useState<BkfcClassifiedError | null>(null);
  const mountedRef = useRef(true);
  // Monotonic id of the latest claim()/reset() call. Every state update in a
  // claim run checks it, so reset() (the approve screen's Back button) cancels
  // an in-flight run's updates, and a retry claim() supersedes the previous
  // run instead of interleaving statuses with it. The superseded promise still
  // settles — a duplicate submission is caught by the gas station's simulation.
  const runRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    // warm well ahead of the click, see ./recaptcha.ts
    warmRecaptchaToken();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const reset = useCallback(() => {
    runRef.current += 1;
    setStatus("idle");
    setError(null);
  }, []);

  const claim = useCallback(
    async (email: string) => {
      if (!account || !uid) return;
      const recipient = account.address.toString();
      const run = ++runRef.current;
      const live = () => mountedRef.current && run === runRef.current;

      setStatus("requesting-signature");
      setError(null);

      let signed: Awaited<ReturnType<typeof fetchClaimSignature>>;
      try {
        signed = await fetchClaimSignature(uid, recipient, email);
      } catch (err) {
        if (!live()) return;
        setError(classifySignerError(err));
        setStatus("error");
        return;
      }

      if (!live()) return;
      setStatus("awaiting-approval");
      try {
        // normally already resolved, warmed on mount. undefined when the
        // site key isn't set — Geomi ignores pluginParams entirely then.
        const recaptchaToken = await getRecaptchaToken();
        // each token is single-use, regenerate now for a possible retry
        warmRecaptchaToken();

        const moduleAddress = requireBkfcModuleAddress();
        const { hash } = await signAndSubmitTransaction({
          data: {
            // public entry fun claim(sender, recipient, uid, expiry_secs, signature_bytes)
            function: `${moduleAddress}::certificate::claim`,
            functionArguments: [
              signed.recipient,
              signed.uid,
              signed.expirySecs,
              // decode hex string to bytes first — passing the string directly
              // aborts with ECLAIM_INVALID_SIGNATURE_LENGTH
              Hex.fromHexString(signed.signature).toUint8Array(),
            ],
          },
          withFeePayer: true,
          pluginParams: { recaptchaToken },
        });

        if (!live()) return;
        setStatus("waiting-confirmation");

        const txn = await getBkfcAptos().waitForTransaction({
          transactionHash: hash,
        });

        if (!isUserTransactionResponse(txn) || !txn.success) {
          throw new Error(
            isUserTransactionResponse(txn)
              ? txn.vm_status
              : "Unexpected transaction type",
          );
        }

        if (!live()) return;
        setStatus("verifying");

        // the backend re-checks the mint on chain, records it, and sends the
        // certificate confirmation email
        let confirmation: BkfcConfirmMintResponse;
        try {
          confirmation = await confirmMint(uid, hash);
        } catch (err) {
          if (!live()) return;
          setError(classifySignerError(err));
          setStatus("error");
          return;
        }

        if (!live()) return;
        setResult({
          hash,
          tokenAddress: confirmation.tokenAddress,
          serial: confirmation.serial,
        });
        setStatus("confirmed");
      } catch (err) {
        if (!live()) return;
        setError(classifyGasStationError(err));
        setStatus("error");
      }
    },
    [account, signAndSubmitTransaction, uid],
  );

  return { claim, status, result, error, reset };
}
